import { loadStripe, type Stripe } from '@stripe/stripe-js';

/** One Stripe.js instance for the whole app. */
export const stripePromise: Promise<Stripe | null> = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

/** Where Stripe sends the browser back after a redirect-based authentication (3D Secure). */
export const PAYMENT_RETURN_PATH = '/billing/payment-return';

export function paymentReturnUrl(): string {
    return `${window.location.origin}${PAYMENT_RETURN_PATH}`;
}

/**
 * What the payment return page must finish when a redirect-based bank authentication brings the
 * browser back. Written to sessionStorage (same tab survives the redirect) right before
 * stripe.confirmSetup / handleNextAction.
 *
 * - `team` - a server-confirmed team charge: call /v1/payments/finalize.
 * - `continuingEducation` - a CE purchase: call continuing-education purchase/finalize.
 * - `signup` - self-serve signup: the return page forwards the Stripe params to
 *   /auth/register/payment, which holds the signup session and resumes there.
 */
export type PendingStripeReturn =
    | { kind: 'team'; teamId: string; teamPaymentId: string }
    | { kind: 'continuingEducation'; paymentMethodType?: string }
    | { kind: 'signup'; paymentMethodType?: string };

const PENDING_KEY = 'calculist.pendingStripeReturn';

export function rememberPendingStripeReturn(pending: PendingStripeReturn): void {
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch {
        // Storage unavailable (private mode, quota). The return page falls back to Stripe's status.
    }
}

export function readPendingStripeReturn(): PendingStripeReturn | null {
    try {
        const raw = sessionStorage.getItem(PENDING_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingStripeReturn;
        return parsed && typeof parsed === 'object' && 'kind' in parsed ? parsed : null;
    } catch {
        return null;
    }
}

export function clearPendingStripeReturn(): void {
    try {
        sessionStorage.removeItem(PENDING_KEY);
    } catch {
        // ignore
    }
}

/** The parts of a Stripe SetupIntent that tell a micro-deposit wait apart from success. */
export interface SetupIntentLike {
    status?: string | null;
    next_action?: { type?: string | null } | null;
}

/**
 * True when stripe.confirmSetup saved a bank account that Stripe will not debit until the customer
 * enters the micro-deposit code. The method exists but cannot be charged yet.
 */
export function isAwaitingMicrodeposits(setupIntent: SetupIntentLike | null | undefined): boolean {
    return !!setupIntent
        && setupIntent.status === 'requires_action'
        && setupIntent.next_action?.type === 'verify_with_microdeposits';
}

/** Shown when a pay-now flow saved a bank account that still needs micro-deposit verification. */
export const MICRODEPOSIT_PAY_NOW_MESSAGE =
    'We sent a small deposit to verify this bank account. It arrives in 1–2 business days; then verify it on Settings → Team → Billing. To pay now, use a card.';

/** Signup has no billing page yet, so the customer must finish with an instantly verified method. */
export const MICRODEPOSIT_SIGNUP_MESSAGE =
    'This bank account needs a small test deposit to verify it, which takes 1–2 business days. To finish signing up now, use a card or sign in to your bank (instant verification).';

/** Shown when a bank account is saved (not charged) and needs micro-deposit verification. */
export const MICRODEPOSIT_SAVED_MESSAGE =
    'Bank account added. Verify it with the deposit code when it arrives (1–2 business days).';

/** The parts of a saved payment method that say whether it can be charged now. */
export interface SavedMethodLike {
    id: string;
    type: string;
    isDefault?: boolean;
    usBankAccount?: { status?: string | null } | null;
}

/** A saved bank account still waiting on its micro-deposit code; Stripe will not debit it yet. */
export function isBankAwaitingVerification(pm: SavedMethodLike): boolean {
    return pm.type === 'us_bank_account' && pm.usBankAccount?.status === 'pending_verification';
}

/**
 * The method to preselect after a bank account turned out to need micro-deposits: the default if
 * it can be charged, else a card, else any chargeable method, else none.
 */
export function chargeableMethodId(methods: SavedMethodLike[]): string | null {
    const chargeable = methods.filter((m) => !isBankAwaitingVerification(m));
    return chargeable.find((m) => m.isDefault)?.id
        ?? chargeable.find((m) => m.type === 'card')?.id
        ?? chargeable[0]?.id
        ?? null;
}
